import React, { FC, useState } from 'react';
import { Autocomplete, Box, Button, FormControl, Grid, TextField } from '@mui/material';
import { LabMaterial } from 'models/material';
import { MaterialMacro } from 'models/material-macro';
import { useMutation } from '@apollo/client';
import { addMaterialToMacroTemplate } from 'apollo/templates/material-macro';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type AddMaterialMacroRowDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    materialMacro: MaterialMacro;
    materials: LabMaterial[];
    materialMacroId?: string;
    onAddMaterial: (materialMacro: MaterialMacro) => void;
};
const AddMaterialMacroRowDialog: FC<AddMaterialMacroRowDialogProps> = ({
    dialogOpen,
    onCloseDialog,
    materialMacro,
    materials,
    materialMacroId,
    onAddMaterial,
}: AddMaterialMacroRowDialogProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings']);
    const [material, setMaterial] = useState<LabMaterial | undefined | null>();
    const { showBoundary } = useDenthubErrorBoundary();

    const [addMaterialToMacro] = useMutation(addMaterialToMacroTemplate, {
        variables: {
            materialMacroId: materialMacroId,
            materialId: material?._id,
        },
        onCompleted: ({ addMaterialToMacro }) => {
            onAddMaterial(addMaterialToMacro);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleMaterialChange = (material?: LabMaterial | null) => {
        setMaterial(material);
    };

    const handleSaveMaterial = () => {
        addMaterialToMacro();
        onCloseDialog();
        setMaterial(undefined);
    };

    // Shown in the option list and in the input once picked. MUI's default filter matches
    // against getOptionLabel, so including the code here is what makes the material
    // findable by code as well as by description.
    const materialLabel = (material: LabMaterial) => {
        return [material?.materialCode, material?.description].filter(Boolean).join(' - ');
    };

    const filteredOptions = (options: LabMaterial[]) => {
        const alreadyAddedMaterialIds = (materialMacro.materials || []).map(
            (macroMaterial) => macroMaterial.material?._id,
        );
        return options.filter((option) => !alreadyAddedMaterialIds.includes(option._id));
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={dialogOpen}
            onClose={onCloseDialog}
            title={translate('lab-settings:ADD_MATERIAL_MACRO_ROW')}
            actions={
                <Button variant="contained" onClick={handleSaveMaterial} disabled={!material}>
                    {translate('common:ADD')}
                </Button>
            }
        >
            <Box mt={1} mb={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <Autocomplete
                                fullWidth
                                id="select-macro-material"
                                options={materials ? filteredOptions(materials) : []}
                                getOptionLabel={(option: LabMaterial) => materialLabel(option)}
                                isOptionEqualToValue={(option: LabMaterial, value: LabMaterial) =>
                                    option?._id === value?._id
                                }
                                renderOption={(props, option: LabMaterial) => (
                                    <li {...{ ...props, key: option?._id }}>{materialLabel(option)}</li>
                                )}
                                onChange={(_event, value) => {
                                    handleMaterialChange(value);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={translate('lab-settings:SELECT_MATERIAL')}
                                        margin="dense"
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
        </DHDialog>
    );
};

export default AddMaterialMacroRowDialog;
